import { isWater, isMountain, isHill } from './interfaces'
import { createHexagon } from './hexagon'
import {
  DirectionalLight,
  AmbientLight,
  InstancedBufferGeometry,
  InstancedBufferAttribute,
  RawShaderMaterial,
  Vector3,
  Vector4,
  Mesh,
  Group,
  Color,
  FrontSide,
  RepeatWrapping,
  ShadowMaterial,
  PlaneGeometry,
  MeshBasicMaterial,
} from 'three'
import { qrToWorld } from './coords'
import Grid from './Grid'
import { LAND_FRAGMENT_SHADER } from './shaders/land.fragment'
import { LAND_VERTEX_SHADER } from './shaders/land.vertex'
import { MOUNTAINS_FRAGMENT_SHADER } from './shaders/mountains.fragment'
import { MOUNTAINS_VERTEX_SHADER } from './shaders/mountains.vertex'
import Forests from './Forests'
export default class MapMesh extends Group {
  /**
   * @param tiles the tiles to actually render in this mesh
   * @param globalGrid the grid with all tiles, including the ones that are not rendered in this mesh
   * @param options map mesh configuration options
   */
  constructor(tiles, options, globalGrid) {
    super()
    this.options = options
    this._showGrid = true
    // use default shaders if none are provided
    if (!options.landFragmentShader) {
      options.landFragmentShader = LAND_FRAGMENT_SHADER
    }
    if (!options.landVertexShader) {
      options.landVertexShader = LAND_VERTEX_SHADER
    }
    if (!options.mountainsFragmentShader) {
      options.mountainsFragmentShader = MOUNTAINS_FRAGMENT_SHADER
    }
    if (!options.mountainsVertexShader) {
      options.mountainsVertexShader = MOUNTAINS_VERTEX_SHADER
    }
    // local, extended copy of tile data
    this.tiles = tiles.map((t) =>
      Object.assign({ bufferIndex: -1, isMountain: isMountain(t.height) }, t)
    )
    this.name = 'MapMesh'
    this.localGrid = new Grid(0, 0).init(this.tiles)
    this.globalGrid = globalGrid || this.localGrid
    options.hillsNormalTexture.wrapS = options.hillsNormalTexture.wrapT = RepeatWrapping
    options.terrainAtlasTexture.wrapS = options.terrainAtlasTexture.wrapT = RepeatWrapping
    options.undiscoveredTexture.wrapS = options.undiscoveredTexture.wrapT = RepeatWrapping
    // options.transitionTexture.flipY = true
    this.loaded = Promise.all([
      this.createLandMesh(this.tiles.filter((t) => !t.isMountain)),
      this.createMountainMesh(this.tiles.filter((t) => t.isMountain)),
    ]).catch((err) => {
      console.error('Could not create MapMesh', err)
      console.error('Error details:', err.message)
      console.error('Error stack:', err.stack)
      throw err // Re-throw to see full error
    })

    this.addPlaneTerrain()
    this.addShadowCatcher()
  }
  get showGrid() {
    return this._showGrid
  }
  set showGrid(value) {
    this._showGrid = value
    const landMaterial = this.land.material
    landMaterial.uniforms.showGrid.value = value ? 1.0 : 0.0
    const mountainMaterial = this.mountains.material
    mountainMaterial.uniforms.showGrid.value = value ? 1.0 : 0.0
  }

  addPlaneTerrain() {
    this.options.planeTerrain.wrapS = RepeatWrapping
    this.options.planeTerrain.wrapT = RepeatWrapping
    this.options.planeTerrain.repeat.set(50, 50)

    let planeGeo = new PlaneGeometry(200, 200, 32)
    let planeMaterial = new MeshBasicMaterial({
      map: this.options.planeTerrain,
    })
    let terrainPlane = new Mesh(planeGeo, planeMaterial)
    terrainPlane.rotateZ(Math.PI / 2)
    terrainPlane.position.setZ(-0.1)

    this.add(terrainPlane)
  }

  addShadowCatcher() {
    var geometry = new PlaneGeometry(512, 512, 32)
    var material = new ShadowMaterial({
      color: 0x103b68,
      opacity: 0.7,
    })
    // var material = new MeshPhongMaterial()
    var mesh = new Mesh(geometry, material)

    mesh.position.z = 0.04
    mesh.receiveShadow = true
    this.add(mesh)
  }
  /**
   * "Hot-swaps" the given textures.
   * @param textures
   */
  replaceTextures(textures) {
    for (let name in textures) {
      const replacement = textures[name]
      if (replacement) {
        const old = this.options[name]
        const { wrapT, wrapS } = old
        old.copy(replacement)
        old.wrapT = wrapT
        old.wrapS = wrapS
        old.needsUpdate = true
      }
    }
  }
  updateTiles(tiles) {
    tiles.map((t) => {
      let old = this.localGrid.get(t.q, t.r)
      if (!old) return
      this.localGrid.add(t.q, t.r, t)
    })
    // this.updateFogAndClouds(tiles)
    // this.trees.updateTiles(tiles)
  }
  getTile(q, r) {
    return this.localGrid.get(q, r)
  }
  /**
   * Updates only fog and clouds visualization of existing tiles.
   * @param tiles changed tiles
   */
  updateFogAndClouds(tiles) {
    const landGeometry = this.land.geometry
    const landStyleAttr = landGeometry.getAttribute('style')
    const mountainsGeometry = this.mountains.geometry
    const mountainsStyleAttr = mountainsGeometry.getAttribute('style')
    tiles.forEach((updated) => {
      const old = this.localGrid.get(updated.q, updated.r)
      if (!old) return
      if (updated.fog != old.fog || updated.clouds != old.clouds) {
        old.fog = updated.fog
        old.clouds = updated.clouds
        const attribute = old.isMountain ? mountainsStyleAttr : landStyleAttr
        this.updateFogStyle(attribute, old.bufferIndex, updated.fog, updated.clouds)
      }
    })
    landStyleAttr.needsUpdate = true
    mountainsStyleAttr.needsUpdate = true
  }
  updateFogStyle(attr, index, fog, clouds) {
    const style = attr.getY(index)
    const fogMask = 0b1
    const newStyle = fog ? style | fogMask : style & ~fogMask
    const withClouds = !clouds ? newStyle % 100 : 100 + newStyle
    attr.setY(index, withClouds)
  }
  async createTrees() {
    const trees = (this.trees = new Forests(this.tiles, this.globalGrid, {
      treeSize: this.options.treeSize || 1.44,
      spritesheet: this.options.treeSpritesheet,
      spritesheetSubdivisions: this.options.treeSpritesheetSubdivisions,
      treesPerForest: this.options.treesPerForest || 50,
      mapScale: this.options.scale || 1.0,
      alphaTest: this.options.treeAlphaTest || 0.2,
      treeOptions: this.options.treeOptions,
    }))
    this.add(trees)
  }
  async createLandMesh(tiles) {
    const atlas = this.options.terrainAtlas
    const geometry = createHexagonTilesGeometry(tiles, this.globalGrid, 0, this.options)
    const isTutorial = this.options.isTutorial
    const material = new RawShaderMaterial({
      uniforms: {
        sineTime: { value: 0.0 },
        showGrid: { value: this._showGrid ? 1.0 : 0.0 },
        camera: { type: 'v3', value: new Vector3(0, 0, 0) },
        texture: { type: 't', value: this.options.terrainAtlasTexture },
        textureAtlasMeta: {
          type: '4f',
          value: new Vector4(atlas.width, atlas.height, atlas.cellSize, atlas.cellSpacing),
        },
        terrainNormal: {
          type: 't',
          value: this.options.terrainNormalTexture,
        },
        hillsNormal: {
          type: 't',
          value: this.options.hillsNormalTexture,
        },
        coastAtlas: {
          type: 't',
          value: this.options.coastAtlasTexture,
        },
        riverAtlas: {
          type: 't',
          value: this.options.riverAtlasTexture,
        },
        mapTexture: {
          type: 't',
          value: this.options.undiscoveredTexture,
        },
        transitionTexture: {
          type: 't',
          value: this.options.transitionTexture,
        },
        lightDir: {
          type: 'v3',
          value: new Vector3(0.5, 0.6, -0.5).normalize(),
        },
        gridColor: {
          type: 'c',
          value:
            typeof this.options.gridColor != 'undefined'
              ? this.options.gridColor
              : isTutorial
              ? new Color(0xffffff)
              : new Color(0xffffff),
        },
        gridWidth: {
          type: 'f',
          value:
            typeof this.options.gridWidth != 'undefined'
              ? this.options.gridWidth
              : isTutorial
              ? 0.04
              : 0.02,
        },
        gridOpacity: {
          type: 'f',
          value:
            typeof this.options.gridOpacity != 'undefined'
              ? this.options.gridOpacity
              : isTutorial
              ? 0.5
              : 0.15,
        },
      },
      vertexShader: this.options.landVertexShader,
      fragmentShader: this.options.landFragmentShader,
      side: FrontSide,
      wireframe: false,
      transparent: false,
    })
    this.land = new Mesh(geometry, material)
    this.land.frustumCulled = false
    this.land.receiveShadow = true

    this.add(this.land)
  }
  async createMountainMesh(tiles) {
    const atlas = this.options.terrainAtlas
    const geometry = createHexagonTilesGeometry(tiles, this.globalGrid, 1, this.options)
    const isTutorial = this.options.isTutorial
    const material = new RawShaderMaterial({
      uniforms: {
        sineTime: { value: 0.0 },
        showGrid: { value: this._showGrid ? 1.0 : 0.0 },
        camera: { type: 'v3', value: new Vector3(0, 0, 0) },
        texture: { type: 't', value: this.options.terrainAtlasTexture },
        textureAtlasMeta: {
          type: '4f',
          value: new Vector4(atlas.width, atlas.height, atlas.cellSize, atlas.cellSpacing),
        },
        hillsNormal: {
          type: 't',
          value: this.options.hillsNormalTexture,
        },
        terrainDark: {
          type: 't',
          value: this.options.terrainDark,
        },
        terrainNormal: {
          type: 't',
          value: this.options.terrainNormalTexture,
        },
        mapTexture: {
          type: 't',
          value: this.options.undiscoveredTexture,
        },
        lightDir: {
          type: 'v3',
          value: new Vector3(0.5, 0.6, -0.5).normalize(),
        },
        gridColor: {
          type: 'c',
          value:
            typeof this.options.gridColor != 'undefined'
              ? this.options.gridColor
              : isTutorial
              ? new Color(0xffffff)
              : new Color(0xffffff),
        },
        gridWidth: {
          type: 'f',
          value:
            typeof this.options.gridWidth != 'undefined'
              ? this.options.gridWidth
              : isTutorial
              ? 0.04
              : 0.02,
        },
        gridOpacity: {
          type: 'f',
          value:
            typeof this.options.gridOpacity != 'undefined'
              ? this.options.gridOpacity
              : isTutorial
              ? 0.5
              : 0.33,
        },
      },
      vertexShader: this.options.mountainsVertexShader,
      fragmentShader: this.options.mountainsFragmentShader,
      side: FrontSide,
      wireframe: false,
      transparent: false,
    })
    this.mountains = new Mesh(geometry, material)
    // this.mountains.castShadow = true
    this.mountains.frustumCulled = false
    this.add(this.mountains)
  }
}
function createHexagonTilesGeometry(tiles, grid, numSubdivisions, options) {
  const scale = options.scale || 1.0
  const hexagon = createHexagon(scale, numSubdivisions)
  const geometry = new InstancedBufferGeometry()
  const textureAtlas = options.terrainAtlas
  geometry.maxInstancedCount = tiles.length
  geometry.setAttribute('position', hexagon.attributes.position)
  geometry.setAttribute('uv', hexagon.attributes.uv)
  geometry.setAttribute('border', hexagon.attributes.border)
  // positions for each hexagon tile
  const tilePositions = tiles.map((tile) => qrToWorld(tile.q, tile.r, scale))
  const posAttr = new InstancedBufferAttribute(
    new Float32Array(tilePositions.length * 2),
    2,
    true,
    1
  )
  // Three.js v0.180.0: copyVector2sArray was removed, use setXY instead
  for (let i = 0; i < tilePositions.length; i++) {
    posAttr.setXY(i, tilePositions[i].x, tilePositions[i].y)
  }
  geometry.setAttribute('offset', posAttr)
  //----------------
  const cellSize = textureAtlas.cellSize
  // const cellSpacing = textureAtlas.cellSpacing
  const numColumns = textureAtlas.width / cellSize
  function terrainCellIndex(terrain) {
    const cell = textureAtlas.textures[terrain]
    return cell.cellY * numColumns + cell.cellX
  }
  const styles = tiles.map(function (tile, index) {
    const cell = textureAtlas.textures[tile.terrain]
    if (!cell) {
      throw new Error(
        `Terrain '${tile.terrain}' not in texture atlas\r\n` + JSON.stringify(textureAtlas)
      )
    }
    const cellIndex = terrainCellIndex(tile.terrain)
    const shadow = tile.fog ? 1 : 0
    const clouds = tile.clouds ? 1 : 0
    const hills = isHill(tile.height) ? 1 : 0
    const style = shadow * 1 + hills * 10 + clouds * 100
    // Coast and River texture index
    const coastIdx = computeCoastTextureIndex(grid, tile)
    const riverIdx = computeRiverTextureIndex(grid, tile)
    tile.bufferIndex = index
    return new Vector4(cellIndex, style, coastIdx, riverIdx)
  })
  const styleAttr = new InstancedBufferAttribute(
    new Float32Array(tilePositions.length * 4),
    4,
    true,
    1
  )
  // Three.js v0.180.0: copyVector4sArray was removed, use setXYZW instead
  for (let i = 0; i < styles.length; i++) {
    styleAttr.setXYZW(i, styles[i].x, styles[i].y, styles[i].z, styles[i].w)
  }
  geometry.setAttribute('style', styleAttr)
  // surrounding tile terrain represented as two consecutive Vector3s
  // 1. [tileIndex + 0] = NE, [tileIndex + 1] = E, [tileIndex + 2] = SE
  // 2. [tileIndex + 0] = SW, [tileIndex + 1] = W, [tileIndex + 2] = NW
  const neighborsEast = new InstancedBufferAttribute(new Float32Array(tiles.length * 3), 3, true, 1)
  const neighborsWest = new InstancedBufferAttribute(new Float32Array(tiles.length * 3), 3, true, 1)
  for (let i = 0; i < tiles.length; i++) {
    const neighbors = grid.surrounding(tiles[i].q, tiles[i].r)
    for (let j = 0; j < neighbors.length; j++) {
      const neighbor = neighbors[j]
      const attr = j > 2 ? neighborsWest : neighborsEast
      const array = attr.array
      // terrain cell index indicates the type of terrain for lookup in the shader
      array[3 * i + (j % 3)] = neighbor ? terrainCellIndex(neighbor.terrain) : -1
    }
  }
  geometry.setAttribute('neighborsEast', neighborsEast)
  geometry.setAttribute('neighborsWest', neighborsWest)
  return geometry
}
function computeCoastTextureIndex(grid, tile) {
  function isWaterTile(q, r) {
    const t = grid.get(q, r)
    if (!t) return false
    return isWater(t.height)
  }
  function bit(x) {
    return x ? '1' : '0'
  }
  if (isWaterTile(tile.q, tile.r)) {
    // only land tiles have a coast
    return 0
  }
  const NE = bit(isWaterTile(tile.q + 1, tile.r - 1))
  const E = bit(isWaterTile(tile.q + 1, tile.r))
  const SE = bit(isWaterTile(tile.q, tile.r + 1))
  const SW = bit(isWaterTile(tile.q - 1, tile.r + 1))
  const W = bit(isWaterTile(tile.q - 1, tile.r))
  const NW = bit(isWaterTile(tile.q, tile.r - 1))
  return parseInt(NE + E + SE + SW + W + NW, 2)
}
function isNextOrPrevRiverTile(grid, tile, q, r, coastCount) {
  const neighbor = grid.get(q, r)
  if (neighbor && neighbor.rivers && tile && tile.rivers) {
    for (let self of tile.rivers) {
      for (let other of neighbor.rivers) {
        const sameRiver =
          self.riverIndex == other.riverIndex &&
          Math.abs(self.riverTileIndex - other.riverTileIndex) == 1
        const otherRiver = self.riverIndex != other.riverIndex && sameRiver
        if (sameRiver || otherRiver) {
          return true
        }
      }
    }
    return false
  } else {
    // let the river run into the first ocean / lake
    if (neighbor && isWater(neighbor.height) && coastCount.count == 0) {
      coastCount.count++
      return true
    } else {
      return false
    }
  }
}
function computeRiverTextureIndex(grid, tile) {
  if (!tile.rivers) return 0
  const coastCount = { count: 0 }
  const NE = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q + 1, tile.r - 1, coastCount))
  const E = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q + 1, tile.r, coastCount))
  const SE = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q, tile.r + 1, coastCount))
  const SW = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q - 1, tile.r + 1, coastCount))
  const W = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q - 1, tile.r, coastCount))
  const NW = bitStr(isNextOrPrevRiverTile(grid, tile, tile.q, tile.r - 1, coastCount))
  const combination = NE + E + SE + SW + W + NW
  return parseInt(combination, 2)
}
function bitStr(x) {
  return x ? '1' : '0'
}
